import backendHttpClient from '@services/_httpClient/backend.http-client';

import type { CreateMedicationUpdateBulaParams } from './dtos/createBula.dto';
import type { CreateMedicationUpdateMessageParams } from './dtos/createMessage.dto';
import type { CreateMedicationUpdateTopicParams } from './dtos/createTopic.dto';
import type { GetAllMedicationUpdatesAvailableParams } from './dtos/getAllAvailable.dto';
import type { GetAllMedicationUpdatesByCollaboratorParams } from './dtos/getAllByCollaborator.dto';
import type { ImportMedicationUpdateTopicsParams } from './dtos/importTopics.dto';
import type { RemoveMedicationUpdateTopicParams } from './dtos/remove.dto';
import type { RemoveAllMedicationUpdateTopicsParams } from './dtos/removeAll.dto';
import type { RemoveMedicationUpdateBulaParams } from './dtos/removeBula.dto';
import type { ReorderMedicationUpdateTopicsParams } from './dtos/reorder.dto';
import type { ReorderMedicationUpdateBulasParams } from './dtos/reorderBulas.dto';
import type { UpdateMedicationUpdateTopicParams } from './dtos/update.dto';
import type { UpdateMedicationUpdateBulaParams } from './dtos/updateBula.dto';
import type { UpdateMedicationUpdateBulaFileParams } from './dtos/updateBulaFile.dto';
import type { UpdateMedicationUpdateMessageParams } from './dtos/updateMessage.dto';
import type { MedicationUpdatesRepository } from './repository';
import type { CreateMessageBody } from './types/createMessage.type';
import type { CreateTopicBody } from './types/createTopic.type';
import type {
  GetAllMedicationUpdatesAvailableOutput,
  GetAllMedicationUpdatesAvailableResponse,
} from './types/getAllAvailable.type';
import type {
  GetAllMedicationUpdatesByCollaboratorOutput,
  GetAllMedicationUpdatesByCollaboratorResponse,
} from './types/getAllByCollaborator.type';
import type {
  GetMedicationUpdateByIdDataResponse,
  GetMedicationUpdateByIdOutput,
} from './types/getById.type';
import type {
  ImportTopicsOutput,
  ImportTopicsResponse,
} from './types/importTopics.type';
import type { ReorderBulasBody } from './types/reorderBulas.type';
import type { ReorderTopicsBody } from './types/reorderTopics.type';
import type { UpdateBulaBody } from './types/updateBula.type';
import type { UpdateMessageBody } from './types/updateMessage.type';
import type { UpdateTopicBody } from './types/updateTopic.type';

const UPLOAD_TIMEOUT = 5 * 60 * 1000;

class MedicationUpdatesService implements MedicationUpdatesRepository {
  public async getAllByCollaborator(
    params?: GetAllMedicationUpdatesByCollaboratorParams,
  ): Promise<GetAllMedicationUpdatesByCollaboratorOutput> {
    const { data } = await backendHttpClient.get<
      GetAllMedicationUpdatesByCollaboratorResponse,
      GetAllMedicationUpdatesByCollaboratorParams
    >({
      path: 'medication-updates/collaborator',
      params,
    });

    const output: GetAllMedicationUpdatesByCollaboratorOutput = {
      list: data.list.map(item => ({
        amount: item.amount,
        startedAt: item.started_at ?? undefined,
        expiredAt: item.expired_at ?? undefined,
        id: item.id,
        priority: item.priority,
        status: item.status,
        title: item.title,
      })),
      pagination: {
        page: params?.page || 1,
        totalPages: data.totalPages ?? 1,
      },
    };
    return output;
  }

  public async getAllAvailable(
    params?: GetAllMedicationUpdatesAvailableParams,
  ): Promise<GetAllMedicationUpdatesAvailableOutput> {
    const { data } = await backendHttpClient.get<
      GetAllMedicationUpdatesAvailableResponse,
      GetAllMedicationUpdatesAvailableParams
    >({
      path: 'medication-updates/available',
      params,
    });

    const output: GetAllMedicationUpdatesAvailableOutput = {
      list: data.list.map(item => ({
        amount: item.amount,
        createdAt: item.created_at,
        id: item.id,
        priority: item.priority,
        status: item.status,
        title: item.title,
      })),
      pagination: {
        page: params?.page || 1,
        totalPages: data.totalPages ?? 1,
      },
    };
    return output;
  }

  public async getById(id: string): Promise<GetMedicationUpdateByIdOutput> {
    const { data: medication } =
      await backendHttpClient.get<GetMedicationUpdateByIdDataResponse>({
        path: `medication-updates/${id}`,
      });
    const output: GetMedicationUpdateByIdOutput = {
      medication: {
        amount: medication.amount,
        bulas: medication.bulas.map(item => ({
          documentDate: item.document_date,
          fileUrl: item.file_url,
          id: item.id,
          label: item.label,
          originUrl: item.origin_url,
          position: item.position,
          referenceNumber: item.reference_number,
          source: item.source,
        })),
        collaborator: medication.collaborator,
        expiredAt: medication.expired_at,
        id: medication.id,
        priority: medication.priority,
        status: medication.status,
        title: medication.title,
        topics: medication.topics.map(item => ({
          content: item.content,
          id: item.id,
          title: item.title ?? '',
        })),
        messages: medication.messages.map(item => ({
          createdAt: item.created_at,
          id: item.id,
          medicationId: item.medication_update_id,
          message: item.message,
          publishedAt: item.published_at,
          type: item.type,
        })),
      },
    };
    return output;
  }

  public async assignCollaborator(id: string): Promise<void> {
    await backendHttpClient.get<void>({
      path: `medication-updates/${id}/collaborator`,
    });
  }

  public async unassignCollaborator(id: string): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `medication-updates/${id}/collaborator`,
    });
  }

  public async removeAllTopics(
    params: RemoveAllMedicationUpdateTopicsParams,
  ): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `medication-updates/${params.medicationUpdateId}/topics`,
    });
  }

  public async removeTopic(
    params: RemoveMedicationUpdateTopicParams,
  ): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `medication-updates/${params.medicationUpdateId}/topics/${params.id}`,
    });
  }

  public async updateTopic(
    params: UpdateMedicationUpdateTopicParams,
  ): Promise<void> {
    await backendHttpClient.put<void, UpdateTopicBody>({
      path: `medication-updates/${params.medicationUpdateId}/topics/${params.id}`,
      body: {
        content: params.content,
        title: params.title,
      },
    });
  }

  public async createMessage(
    params: CreateMedicationUpdateMessageParams,
  ): Promise<void> {
    await backendHttpClient.post<void, CreateMessageBody>({
      path: `medication-updates/${params.medicationUpdateId}/messages`,
      body: {
        message: params.message,
      },
    });
  }

  public async updateMessage(
    params: UpdateMedicationUpdateMessageParams,
  ): Promise<void> {
    await backendHttpClient.put<void, UpdateMessageBody>({
      path: `medication-updates/${params.medicationUpdateId}/messages/${params.medicationUpdateMessageId}`,
      body: {
        message: params.message,
      },
    });
  }

  public async createTopic(
    params: CreateMedicationUpdateTopicParams,
  ): Promise<void> {
    await backendHttpClient.post<void, CreateTopicBody>({
      path: `medication-updates/${params.medicationUpdateId}/topics`,
      body: {
        content: params.content,
        position: params.position,
        title: params.title,
      },
    });
  }

  public async importTopics(
    params: ImportMedicationUpdateTopicsParams,
  ): Promise<ImportTopicsOutput> {
    const formData = new FormData();

    formData.append('file', params.file);

    const { data } = await backendHttpClient.post<
      ImportTopicsResponse,
      FormData
    >({
      path: `medication-updates/${params.medicationUpdateId}/topics/import`,
      body: formData,
      timeout: UPLOAD_TIMEOUT,
    });

    return {
      bulasAdded: data.bulasAdded,
      bulasReplaced: data.bulasReplaced,
      topicsCreated: data.topicsCreated,
      warnings: data.warnings,
    };
  }

  public async reorderTopics(
    params: ReorderMedicationUpdateTopicsParams,
  ): Promise<void> {
    await backendHttpClient.patch<void, ReorderTopicsBody>({
      path: `medication-updates/${params.medicationUpdateId}/topics`,
      body: {
        ids: params.ids,
      },
    });
  }

  public async markReview(id: string): Promise<void> {
    await backendHttpClient.get<void>({
      path: `medication-updates/${id}/review`,
    });
  }

  public async createBula(
    params: CreateMedicationUpdateBulaParams,
  ): Promise<void> {
    const formData = new FormData();

    // campos vazios ficam de fora; o arquivo vai por último
    const fields: Record<string, string | number | undefined> = {
      document_date: params.documentDate,
      label: params.label,
      origin_url: params.originUrl,
      reference_number: params.referenceNumber,
      source: params.source,
    };
    for (const [key, value] of Object.entries(fields)) {
      if (value !== undefined && value !== '') {
        formData.append(key, String(value));
      }
    }
    formData.append('file', params.file);

    await backendHttpClient.post<void, FormData>({
      path: `medication-updates/${params.medicationUpdateId}/bulas`,
      body: formData,
      timeout: UPLOAD_TIMEOUT,
    });
  }

  public async updateBula(
    params: UpdateMedicationUpdateBulaParams,
  ): Promise<void> {
    await backendHttpClient.put<void, UpdateBulaBody>({
      path: `medication-updates/${params.medicationUpdateId}/bulas/${params.id}`,
      body: {
        document_date: params.documentDate || null,
        label: params.label,
        origin_url: params.originUrl || null,
        reference_number: params.referenceNumber ?? null,
        source: params.source ?? null,
      },
    });
  }

  public async updateBulaFile(
    params: UpdateMedicationUpdateBulaFileParams,
  ): Promise<void> {
    const formData = new FormData();

    formData.append('file', params.file);

    await backendHttpClient.put<void, FormData>({
      path: `medication-updates/${params.medicationUpdateId}/bulas/${params.id}/file`,
      body: formData,
      timeout: UPLOAD_TIMEOUT,
    });
  }

  public async removeBula(
    params: RemoveMedicationUpdateBulaParams,
  ): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `medication-updates/${params.medicationUpdateId}/bulas/${params.id}`,
    });
  }

  public async reorderBulas(
    params: ReorderMedicationUpdateBulasParams,
  ): Promise<void> {
    await backendHttpClient.patch<void, ReorderBulasBody>({
      path: `medication-updates/${params.medicationUpdateId}/bulas`,
      body: {
        ids: params.ids,
      },
    });
  }
}

export const medicationUpdatesService = new MedicationUpdatesService();
